/**
 * Settings → Questions → Estimate: the rough price range a Customer gets
 * before the quote, per form — shown once on the form's review step, and
 * said on the phone by the Receptionist at the end of a call (ADR-0025).
 *
 * One switch for both, and no second one here on purpose: a Company
 * decides once whether a Customer sees a range before the quote, and a
 * caller is not a lesser Customer for having rung instead of typed.
 *
 * The copy says which two surfaces, because this screen is the only
 * place a Company learns that the switch reaches its phone as well. It
 * names the form and the call rather than "everywhere": a Company that
 * cannot tell where a price it turned on is being said is one that
 * leaves it off.
 *
 * A page under Questions rather than a section beside it. The range is
 * what the answers add up to, so it is the same subject one step on; and
 * it is set on the day a Company starts and left alone after, which is
 * what earns a page rather than a row in the sidebar (DESIGN.md, Settings
 * → "A section is one screen, and a page is one step under one").
 *
 * Two numbers and a switch. What the range is priced from is the gear
 * the model chooses for the enquiry, at the rental system's own rates
 * (ADR-0030), so there is nothing here about which gear an answer asks
 * for. There is no preview either: a range is a model call over one
 * enquiry, and three sample jobs priced on every visit to this page would
 * be three calls whose answers change from one visit to the next.
 */
import { Banner } from "../../components/ui/Banner";
import {
  NumberSettingRow,
  ToggleSettingRow,
} from "../../components/ui/SettingRow";
import {
  listInquiryProfileEstimates,
  saveEstimateConfig,
  type EstimateConfig,
  type InquiryProfileEstimate,
} from "../../lib/settings-api";
import { useSettingsSource } from "../../lib/use-settings-source";
import { Loading, SectionHeader, SettingsGroup } from "./section-chrome";
import styles from "./EstimateSettings.module.css";

export function EstimateSettings() {
  const {
    data: profiles,
    loadError: error,
    reload,
  } = useSettingsSource(listInquiryProfileEstimates);

  return (
    <div>
      <SectionHeader
        title="Estimate"
        description="The price range a customer sees before the quote."
      />
      {error && (
        <Banner variant="error" className={styles.notice}>
          {error}
        </Banner>
      )}
      {profiles === null ? (
        !error && <Loading />
      ) : profiles.length === 0 ? (
        <p className={styles.empty}>
          You have no inquiry forms yet, so there is nothing to price.
        </p>
      ) : (
        <div className={styles.profiles}>
          {profiles.map((profile) => (
            <ProfileEstimate
              key={profile.profile_key}
              profile={profile}
              titled={profiles.length > 1}
              onSaved={() => void reload()}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ProfileEstimate({
  profile,
  titled,
  onSaved,
}: {
  profile: InquiryProfileEstimate;
  /** False for a Company with one form: naming the only form names nothing. */
  titled: boolean;
  onSaved: () => void;
}) {
  const cfg = profile.estimate_config;
  const key = profile.profile_key;

  // One write for the whole config, whichever field moved: the server
  // reads it as one object, and the list is refetched after so the rows
  // show what was just saved.
  const save = async (next: EstimateConfig) => {
    await saveEstimateConfig(key, next);
    onSaved();
  };


  const rows = (
    <>
      <ToggleSettingRow
        id={`estimate-enabled-${key}`}
        title="Show a price range"
        label={`Show a price range for ${profile.label}`}
        checked={cfg.enabled}
        onChange={(enabled) => save({ ...cfg, enabled })}
      />

      {cfg.enabled && (
        <>
          <NumberSettingRow
            id={`estimate-floor-${key}`}
            title="Minimum"
            description="The range never goes below this."
            tooltip="Your callout minimum: the smallest job worth sending a van for."
            indent
            value={cfg.floor}
            min={0}
            onSave={(floor) => save({ ...cfg, floor })}
          />
          <NumberSettingRow
            id={`estimate-spread-${key}`}
            title="Spread"
            description="How wide the range is. 20 shows $500 as $450 – $550."
            tooltip="A percentage of the midpoint, split either side of it."
            indent
            value={cfg.spreadPercent}
            min={0}
            max={100}
            onSave={(spreadPercent) => save({ ...cfg, spreadPercent })}
          />
        </>
      )}
    </>
  );

  if (!titled) return rows;
  return (
    <SettingsGroup
      id={`estimate-${key}`}
      title={profile.label}
      description={`/${profile.route_slug}`}
    >
      {rows}
    </SettingsGroup>
  );
}
